import { useMemo } from "react";
import type { RunProjection } from "@roboscientist/contracts";
import { OverflowReveal } from "@roboscientist/ui";
import { progressValue } from "./runPresentation.js";

const updatedAt = (run: RunProjection) => Date.parse(run.updatedAt);
const runName = (run: RunProjection) => run.id.split(":").at(-1) ?? run.id;
const hasTelemetryContract = (run: RunProjection) =>
  run.capabilities.metrics || run.capabilities.metricsDeclared;
export const telemetryState = (run: RunProjection) => {
  const active = run.status === "active" || run.status === "running";
  const terminalOnly = run.capabilities.metricsAvailability === "terminal";
  if (run.capabilities.metrics) {
    if (terminalOnly) return "Terminal telemetry available";
    return active ? "Streaming" : "Telemetry available";
  }
  if (run.capabilities.metricsDeclared) {
    if (terminalOnly) return active ? "Available after completion" : "Terminal telemetry missing";
    return active ? "Waiting for first metric" : "Declared telemetry missing";
  }
  return "No telemetry contract";
};

export const telemetryRuns = (runs: RunProjection[]) => [...runs]
  .filter(hasTelemetryContract)
  .sort((a, b) => updatedAt(b) - updatedAt(a));

export const pickTelemetryRun = (runs: RunProjection[]) => {
  const newest = (values: RunProjection[]) => [...values].sort((a, b) => updatedAt(b) - updatedAt(a))[0] ?? null;
  return newest(runs.filter((run) => (run.status === "active" || run.status === "running") && hasTelemetryContract(run)))
    ?? newest(runs.filter((run) => run.capabilities.metrics))
    ?? newest(runs.filter((run) => run.capabilities.metricsDeclared))
    ?? null;
};

export function TelemetryRunSelector({ runs, selectedRunId, onSelect }: {
  runs: RunProjection[];
  selectedRunId: string | null;
  onSelect: (runId: string) => void;
}) {
  const candidates = useMemo(() => telemetryRuns(runs), [runs]);
  const selected = runs.find((run) => run.id === selectedRunId) ?? null;
  const options = selected && !candidates.some((run) => run.id === selected.id) ? [selected, ...candidates] : candidates;
  if (!options.length) return null;
  return <div className="telemetry-run-selector">
    <div className="telemetry-run-selector__summary"><span>CURVE RUNS</span><strong>{candidates.length}</strong><small>Select a Run to replace the chart</small></div>
    <div className="telemetry-run-selector__rail" role="listbox" aria-label="Select Run curve">
      {options.map((run) => <button key={run.id} role="option" aria-selected={run.id === selectedRunId} className={run.id === selectedRunId ? "selected" : ""} aria-label={`${runName(run)}, ${run.path}`} onClick={() => onSelect(run.id)}>
        <i className={`run-kind-dot run-kind-dot--${run.kind}`} /><OverflowReveal as="span" value={run.path} displayValue={runName(run)} kind="path" label="Run path" copyable /><small>{telemetryState(run)}{run.progress.current == null ? "" : " · " + progressValue(run)}</small>
      </button>)}
    </div>
  </div>;
}
